import * as THREE from 'three';
import { tuning } from '@/config/tuning';
import { shortestAngleDelta } from '@/math/angles';
import { forwardX, forwardZ } from '@/math/orientation';

/** What the camera needs to know about whatever it's chasing. */
export interface ChaseTarget {
  x: number;
  y: number;
  z: number;
  heading: number;
  speed: number;
}

/**
 * ChaseCamera.ts — a smoothed camera that sits behind the car (§5.10, §6).
 *
 * The camera's own yaw eases toward the car's heading via the SHORTEST angle, so
 * a spin or a sharp flick doesn't whip the camera around. Distance grows a touch
 * with speed; height follows the ground; it looks slightly ahead of the car.
 */
export class ChaseCamera {
  private camYaw = 0;
  private pos = new THREE.Vector3();
  private look = new THREE.Vector3();
  private initialized = false;

  /** Target FOV (degrees). Power-ups will nudge this for a speed "punch". */
  fov = tuning.camera.fovBase;
  /** M145: extra distance to fall back while the car boosts (world units).
   *  Eased, so the car surges away from the camera and is reeled back in. */
  boostPull = 0;
  private pull = 0;

  update(camera: THREE.PerspectiveCamera, target: ChaseTarget, dt: number): void {
    const cfg = tuning.camera;
    if (!this.initialized) {
      this.warp(camera, target);
      return;
    }

    this.pull += (this.boostPull - this.pull) * (1 - Math.exp(-cfg.boostPullRate * dt));
    const distance = cfg.back + Math.max(0, target.speed) * cfg.backPerSpeed + this.pull;

    // Ease the camera's yaw toward the car heading (shortest way round).
    this.camYaw += shortestAngleDelta(this.camYaw, target.heading) * (1 - Math.exp(-cfg.yawFollow * dt));

    const fx = forwardX(this.camYaw);
    const fz = forwardZ(this.camYaw);
    const desiredX = target.x - fx * distance;
    const desiredZ = target.z - fz * distance;
    // M145: the camera also sinks a touch while pulled back — more road rush.
    const desiredY = target.y + cfg.up - this.pull * cfg.boostDrop;

    // Damp the position toward the ideal spot (responsive, slight lag).
    const k = 1 - Math.exp(-12 * dt);
    this.pos.x += (desiredX - this.pos.x) * k;
    this.pos.y += (desiredY - this.pos.y) * k;
    this.pos.z += (desiredZ - this.pos.z) * k;
    camera.position.copy(this.pos);

    // Look slightly ahead of the car along its own heading.
    this.look.set(
      target.x + forwardX(target.heading) * cfg.lookAhead,
      target.y + 1.2,
      target.z + forwardZ(target.heading) * cfg.lookAhead,
    );
    camera.lookAt(this.look);

    // Ease FOV toward target (M7 boost will raise the target briefly).
    camera.fov += (this.fov - camera.fov) * (1 - Math.exp(-8 * dt));
    camera.updateProjectionMatrix();
  }

  /** Snap instantly behind the target (race start / respawn — no fly-in). */
  warp(camera: THREE.PerspectiveCamera, target: ChaseTarget): void {
    const cfg = tuning.camera;
    this.initialized = true;
    this.pull = 0;
    this.camYaw = target.heading;
    const distance = cfg.back + Math.max(0, target.speed) * cfg.backPerSpeed;
    this.pos.set(
      target.x - forwardX(target.heading) * distance,
      target.y + cfg.up,
      target.z - forwardZ(target.heading) * distance,
    );
    camera.position.copy(this.pos);
    camera.lookAt(target.x, target.y + 1.2, target.z);
    camera.fov = this.fov;
    camera.updateProjectionMatrix();
  }
}
